/**
 * Minimum-order-value storefront guard.
 *
 * The visible state - the banner, the disabled checkout button, the hidden express-checkout wrapper
 * - is rendered server-side in `snippets/cart-summary.liquid`. That is deliberate: the cart page and
 * the cart drawer are both re-rendered through the Section Rendering API on every cart mutation, so
 * server-rendered state stays correct after each morph and still works with JavaScript disabled.
 *
 * This module is a narrow secondary safety net for checkout controls inside server-rendered active
 * gates. It does not claim to validate Shopify checkout itself; direct navigation, external sales
 * channels, and programmatic checkout require platform-side validation.
 */

/** Only a gate whose server-rendered state says "blocked" intercepts anything. */
const ACTIVE_CART_GATE = 'min-order-gate[data-min-order-gate="active"]';

/** Product express checkout remains mounted but hidden so its initialized Shopify element is stable. */
const ACTIVE_PRODUCT_GATE = '[data-min-order-product-restricted="active"]';

/** Elements that begin, or lead to, checkout. */
const CHECKOUT_TRIGGER = [
  '[name="checkout"]',
  '.additional-checkout-buttons',
  'shopify-accelerated-checkout-cart',
  'a[href*="/checkout"]',
].join(', ');

/**
 * Cancels checkout clicks while the minimum order value is unmet.
 *
 * Registered on `document` in the capture phase so it runs before listeners bound further down the
 * tree, including those an app attaches to its own button.
 *
 * @param {MouseEvent} event
 */
function guardCheckoutClick(event) {
  const { target } = event;
  if (!(target instanceof Element)) return;

  const productGate = target.closest(ACTIVE_PRODUCT_GATE);
  if (productGate) {
    event.preventDefault();
    event.stopImmediatePropagation();

    productGate
      .closest('product-form-component')
      ?.querySelector('.min-order-notice')
      ?.scrollIntoView({ block: 'nearest' });
    return;
  }

  const cartGate = target.closest(ACTIVE_CART_GATE);
  if (!cartGate) return;

  const trigger = target.closest(CHECKOUT_TRIGGER);
  if (!trigger || !cartGate.contains(trigger)) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  cartGate.querySelector('.min-order-gate')?.scrollIntoView({ block: 'nearest' });
}

document.addEventListener('click', guardCheckoutClick, { capture: true });

/**
 * Registered so the element is a defined custom element rather than an upgrade-pending unknown one.
 * All behaviour lives in the document-level guard above.
 */
class MinOrderGate extends HTMLElement {}

if (!customElements.get('min-order-gate')) {
  customElements.define('min-order-gate', MinOrderGate);
}
